/* ============================================================
   <pak-quickview> — bottom sheet (mobile) / modal (desktop)
   for PDP Acne Kit product cards. Clicking a card opens a
   quick-view populated from that card's hidden <template>.
   Layout referenced from <mps-included-drawer>; no add-to-cart,
   only a ghost "View Product" link.
   ============================================================ */

class PakQuickview extends HTMLElement {
  connectedCallback() {
    /* Shell dedup — each pdp-acne-kit section mounts a shell. If several land
       on one page we keep the first active and disable the rest so a single
       shell serves every card's template (templates are keyed by section+block). */
    if (window.__pakQvActive) {
      this.setAttribute('hidden', '');
      return;
    }
    window.__pakQvActive = this;

    this.contentEl = this.querySelector('[data-pak-qv-content]');
    this.scrollEl = this.querySelector('[data-pak-qv-scroll]');
    this.backdropEl = this.querySelector('[data-pak-qv-backdrop]');
    this.viewLinkEl = this.querySelector('[data-pak-qv-view]');

    this._lastFocusedTrigger = null;

    this._bindGlobalTriggers();
    this._bindCloseInteractions();
  }

  /* ---- Open from any [data-pak-qv-trigger] (the card) ---- */
  _bindGlobalTriggers() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-pak-qv-trigger]');
      if (!trigger) return;
      /* Let the in-card controls keep working without opening the popup. */
      if (event.target.closest('.pdp-acne-kit__dots, .pdp-acne-kit__more, a, button, video')) return;
      event.preventDefault();
      this.open(trigger);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const trigger = event.target.closest?.('[data-pak-qv-trigger]');
      if (!trigger || trigger !== event.target) return;
      event.preventDefault();
      this.open(trigger);
    });
  }

  _bindCloseInteractions() {
    this.querySelectorAll('[data-pak-qv-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });
    this.backdropEl.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  /* ---- Open ---- */
  open(trigger) {
    const tplKey = trigger.dataset.pakQvTplKey;
    const tpl = document.querySelector(
      `template[data-pak-qv-template][data-pak-qv-tpl-key="${CSS.escape(tplKey)}"]`
    );
    if (!tpl) {
      console.warn('[pak-qv] template not found for', tplKey);
      return;
    }

    this._lastFocusedTrigger = trigger;

    /* Inject pre-rendered content (cloned from <template>, no innerHTML build). */
    this.contentEl.replaceChildren(tpl.content.cloneNode(true));

    if (this.viewLinkEl) this.viewLinkEl.href = tpl.dataset.productUrl || '#';

    if (this.scrollEl) this.scrollEl.scrollTop = 0;
    this._initSliderScrollBar();

    this.removeAttribute('hidden');
    this.setAttribute('aria-hidden', 'false');
    /* Force a reflow so the transition kicks in from the off-screen state. */
    void this.offsetWidth;
    this.classList.add('is-open');

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      const closeBtn = this.querySelector('[data-pak-qv-close]');
      if (closeBtn) closeBtn.focus();
    });
  }

  /* ---- Close ---- */
  close() {
    if (!this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');

    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';

    const finish = () => {
      this.setAttribute('hidden', '');
      this.contentEl.replaceChildren();
      if (this._lastFocusedTrigger) {
        try {
          this._lastFocusedTrigger.focus();
        } catch (_) {}
        this._lastFocusedTrigger = null;
      }
    };

    const sheet = this.querySelector('.pak-qv__sheet');
    if (sheet) {
      let done = false;
      const onEnd = () => {
        if (done) return;
        done = true;
        sheet.removeEventListener('transitionend', onEnd);
        finish();
      };
      sheet.addEventListener('transitionend', onEnd);
      /* Fallback in case transitionend doesn't fire (e.g. reduced motion). */
      setTimeout(onEnd, 360);
    } else {
      finish();
    }
  }

  /* ---- Carousel scroll-bar (mirrors the mps-ipd pattern) ---- */
  _initSliderScrollBar() {
    const slider = this.contentEl.querySelector('[data-pak-qv-slider]');
    const bar = this.contentEl.querySelector('[data-pak-qv-scroll-bar]');
    if (!slider || !bar) return;

    const update = () => {
      const maxScroll = slider.scrollWidth - slider.clientWidth;
      if (maxScroll <= 0) {
        bar.style.transform = 'translateX(0)';
        return;
      }
      const pct = slider.scrollLeft / maxScroll;
      const trackWidth = bar.parentElement.clientWidth;
      bar.style.transform = `translateX(${pct * (trackWidth - 15)}px)`;
    };

    slider.addEventListener('scroll', update, { passive: true });
    update();
  }
}

if (!customElements.get('pak-quickview')) {
  customElements.define('pak-quickview', PakQuickview);
}
